/** Carrier yaratish / tahrirlash oynasi (owner va filial menejeri). */
import { useEffect, useState } from "react";
import { ApiError } from "@/lib/api";
import { US_STATES } from "@/features/drivers/types";
import { useCreateCarrier, useUpdateCarrier } from "@/features/carriers/api";
import { CARRIER_STATUS_LABEL, type Carrier, type CarrierStatus, type CarrierWrite } from "@/features/carriers/types";
import { Button } from "@/components/ui/button";
import { Checkbox, Field, Input, NumberInput, Select, Textarea } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carrier?: Carrier | null;
  onSaved?: (carrier: Carrier) => void;
}

type FormState = Required<Omit<CarrierWrite, "hiring_states" | "status">> & { hiring_states: string[]; status: CarrierStatus };

const EMPTY: FormState = {
  name: "",
  mc_number: "",
  dot_number: "",
  status: "active",
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  city: "",
  state: "",
  hiring_states: [],
  submission_method: "",
  reply_time: "",
  placement_fee: "",
  notes: "",
};

function fromCarrier(c: Carrier): FormState {
  return {
    name: c.name,
    mc_number: c.mc_number,
    dot_number: c.dot_number,
    status: c.status,
    contact_name: c.contact_name,
    contact_phone: c.contact_phone,
    contact_email: c.contact_email,
    city: c.city,
    state: c.state,
    hiring_states: c.hiring_states,
    submission_method: c.submission_method,
    reply_time: c.reply_time,
    placement_fee: c.placement_fee ?? "",
    notes: c.notes,
  };
}

export function CarrierFormModal({ open, onOpenChange, carrier, onSaved }: Props) {
  const { t } = useTranslation("carriers");
  const editing = !!carrier;
  const [form, setForm] = useState<FormState>(carrier ? fromCarrier(carrier) : EMPTY);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const create = useCreateCarrier();
  const update = useUpdateCarrier(carrier?.id ?? 0);
  const busy = create.isPending || update.isPending;

  useEffect(() => {
    if (open) {
      setForm(carrier ? fromCarrier(carrier) : EMPTY);
      setErrors({});
    }
  }, [open, carrier]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));
  const allStates = form.hiring_states.includes("ALL");

  function toggleState(code: string) {
    setForm((f) => {
      const without = f.hiring_states.filter((s) => s !== "ALL");
      return { ...f, hiring_states: without.includes(code) ? without.filter((s) => s !== code) : [...without, code] };
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});
    const local: Record<string, string[]> = {};
    if (!form.name.trim()) local.name = [t("form.nameRequired")];
    if (Object.keys(local).length) {
      setErrors(local);
      return;
    }
    try {
      // MC ixtiyoriy: mijozning hamkorlar ro'yxatida MC yo'q; summa bo'sh bo'lsa -- null
      const payload = { ...form, placement_fee: form.placement_fee === "" ? null : form.placement_fee };
      const saved = editing && carrier ? await update.mutateAsync({ ...payload, version: carrier.version }) : await create.mutateAsync(payload);
      toast.success(editing ? t("form.saved") : t("form.created"));
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.isConflict) {
          toast.error(err.changedBy ? t("common:toast.conflict", { who: err.changedBy }) : t("common:toast.conflictAnon"));
          onOpenChange(false);
          return;
        }
        if (err.fields) setErrors(err.fields);
        else toast.error(err.message);
      } else toast.error(t("form.saveFailed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t("form.editTitle") : t("form.newTitle")}
      size="lg"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={busy} onClick={(e) => submit(e as unknown as React.FormEvent)}>
            {editing ? t("common:actions.saveChanges") : t("common:actions.save")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("form.name")} required error={errors.name} className="col-span-2">
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus aria-invalid={!!errors.name} />
          </Field>
          <Field label={t("form.mc")} error={errors.mc_number}>
            <Input value={form.mc_number} onChange={(e) => set("mc_number", e.target.value)} placeholder="MC 900689" aria-invalid={!!errors.mc_number} />
          </Field>
          <Field label={t("form.dot")} error={errors.dot_number}>
            <Input value={form.dot_number} onChange={(e) => set("dot_number", e.target.value)} placeholder="DOT 3900689" />
          </Field>
          {editing && (
            <Field label={t("form.status")} error={errors.status}>
              <Select value={form.status} onChange={(e) => set("status", e.target.value as CarrierStatus)}>
                {(Object.keys(CARRIER_STATUS_LABEL) as CarrierStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {t(`common:status.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.mainContact")} error={errors.contact_name}>
            <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Nina Patel" />
          </Field>
          <Field label={t("form.phone")} error={errors.contact_phone}>
            <Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="(555) 010-0206" />
          </Field>
          <Field label={t("form.email")} error={errors.contact_email}>
            <Input type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="nina.p@example.com" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.city")} error={errors.city}>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
          </Field>
          <Field label={t("form.state")} error={errors.state}>
            <Select value={form.state} onChange={(e) => set("state", e.target.value)}>
              <option value="">—</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("form.replyTime")} error={errors.reply_time}>
            <Input value={form.reply_time} onChange={(e) => set("reply_time", e.target.value)} placeholder={t("form.replyPlaceholder")} />
          </Field>
        </div>

        <Field label={t("form.hiringStates")} error={errors.hiring_states} hint={allStates ? t("form.all48") : form.hiring_states.length ? form.hiring_states.join(", ") : t("form.pickStates")}>
          <div className="rounded-[10px] border border-border p-3">
            <Checkbox label={t("form.all48")} checked={allStates} onChange={(e) => set("hiring_states", e.target.checked ? ["ALL"] : [])} className="mb-2 font-semibold" />
            <div className="grid grid-cols-10 gap-x-2 gap-y-1">
              {US_STATES.map((code) => (
                <Checkbox key={code} label={code} checked={!allStates && form.hiring_states.includes(code)} disabled={allStates} onChange={() => toggleState(code)} className="text-[12px]" />
              ))}
            </div>
          </div>
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label={t("form.submissionMethod")} error={errors.submission_method} hint={t("form.submissionHint")} className="col-span-2">
            <Input value={form.submission_method} onChange={(e) => set("submission_method", e.target.value)} placeholder={t("form.submissionPlaceholder")} />
          </Field>
          <Field label={t("form.placementFee")} error={errors.placement_fee} hint={t("form.placementFeeHint")}>
            <NumberInput min={0} step="1" value={form.placement_fee ?? ""} onChange={(e) => set("placement_fee", e.target.value)} placeholder="1,500" />
          </Field>
        </div>
        <Field label={t("form.notes")} error={errors.notes}>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("form.notesPlaceholder")} />
        </Field>
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  );
}
